import React, { useState } from 'react';
import { Modal, Input, Button, Alert, Spin, Collapse, Tag, Space } from 'antd';
import { SearchOutlined, CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useTranslationWithNamespace } from '../../hooks/useTranslationWithNamespace';
import { useSearchJob } from '../../hooks/useSearchJob';
import JSONPretty from 'react-json-pretty';
import 'react-json-pretty/themes/monikai.css';
import { showErrorNotification } from '../../utils/notificationUtils';

const { Panel } = Collapse;

interface JobSearchModalProps {
  visible: boolean;
  onClose: () => void;
  onJobFound: (job: any, queueName: string, jobType: string) => void;
}

const JobSearchModal: React.FC<JobSearchModalProps> = ({
  visible,
  onClose,
  onJobFound
}) => {
  const { t: commonT } = useTranslation();
  const { t: queueT } = useTranslationWithNamespace('queue');
  const [jobId, setJobId] = useState('');
  const [foundJob, setFoundJob] = useState<any>(null);

  // Search job mutation
  const searchJobMutation = useSearchJob();

  // Handle search
  const handleSearch = () => {
    if (!jobId.trim()) {
      showErrorNotification(queueT('error.emptyJobId', 'Please enter a job ID'));
      return;
    }

    searchJobMutation.mutate(jobId, {
      onSuccess: (data) => {
        setFoundJob(data.job);
      },
      onError: (error: any) => {
        showErrorNotification(
          queueT('error.jobNotFound', 'Job not found'),
          error?.response?.data?.error || error?.message || queueT('error.unknown', 'An unknown error occurred')
        );
      }
    });
  };

  // Format timestamp
  const formatTimestamp = (timestamp: number) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleString();
  };

  // Format job duration
  const formatDuration = (startTime?: number, endTime?: number) => {
    if (!startTime || !endTime) return '-';
    const duration = endTime - startTime;
    if (duration < 1000) return `${duration}ms`;
    return `${(duration / 1000).toFixed(2)}s`;
  };

  // Render job status tag
  const renderStatusTag = (status: string) => {
    switch (status) {
      case 'completed':
        return <Tag color="success" icon={<CheckCircleOutlined />}>{queueT('jobStatus.completed')}</Tag>;
      case 'failed':
        return <Tag color="error" icon={<CloseCircleOutlined />}>{queueT('jobStatus.failed')}</Tag>;
      case 'active':
        return <Tag color="processing" icon={<ClockCircleOutlined />}>{queueT('jobStatus.active')}</Tag>;
      case 'waiting':
        return <Tag color="default" icon={<ClockCircleOutlined />}>{queueT('jobStatus.waiting')}</Tag>;
      default:
        return <Tag color="default">{status}</Tag>;
    }
  };

  // Handle view job details
  const handleViewDetails = () => {
    if (foundJob) {
      onJobFound(foundJob, foundJob.queueName, foundJob.jobType);
      onClose();
    }
  };

  // Reset state when modal closes
  const handleClose = () => {
    setJobId('');
    setFoundJob(null);
    onClose();
  };

  return (
    <Modal
      title={queueT('searchJob.title', 'Search Job by ID')}
      open={visible}
      onCancel={handleClose}
      footer={[
        <Button key="close" onClick={handleClose}>
          {commonT('actions.close')}
        </Button>,
        foundJob && (
          <Button key="view" type="primary" onClick={handleViewDetails}>
            {queueT('searchJob.viewDetails', 'View Details')}
          </Button>
        )
      ]}
      width={700}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <Input.Search
          placeholder={queueT('searchJob.placeholder', 'Enter job ID')}
          value={jobId}
          onChange={(e) => setJobId(e.target.value)}
          onSearch={handleSearch}
          enterButton={<Button icon={<SearchOutlined />} type="primary">{queueT('searchJob.search', 'Search')}</Button>}
          loading={searchJobMutation.isPending}
        />

        {searchJobMutation.isPending && (
          <div style={{ textAlign: 'center', margin: '20px 0' }}>
            <Spin />
            <div>{queueT('searchJob.searching', 'Searching for job...')}</div>
          </div>
        )}

        {foundJob && (
          <div style={{ marginTop: 16 }}>
            <Alert
              message={queueT('searchJob.jobFound', 'Job Found')}
              description={
                <div>
                  <p>
                    <strong>{queueT('searchJob.queue', 'Queue')}:</strong> {foundJob.queueName}
                  </p>
                  <p>
                    <strong>{queueT('searchJob.status', 'Status')}:</strong> {renderStatusTag(foundJob.jobType)}
                  </p>
                  <p>
                    <strong>{queueT('searchJob.timestamp', 'Created')}:</strong> {formatTimestamp(foundJob.timestamp)}
                  </p>
                  {foundJob.processedOn && (
                    <p>
                      <strong>{queueT('searchJob.processedOn', 'Processed')}:</strong> {formatTimestamp(foundJob.processedOn)}
                    </p>
                  )}
                  {foundJob.finishedOn && (
                    <p>
                      <strong>{queueT('searchJob.finishedOn', 'Finished')}:</strong> {formatTimestamp(foundJob.finishedOn)}
                    </p>
                  )}
                  {foundJob.processedOn && foundJob.finishedOn && (
                    <p>
                      <strong>{queueT('searchJob.duration', 'Duration')}:</strong> {formatDuration(foundJob.processedOn, foundJob.finishedOn)}
                    </p>
                  )}
                  <p>
                    <strong>{queueT('searchJob.attempts', 'Attempts')}:</strong> {foundJob.attemptsMade}
                  </p>
                </div>
              }
              type="success"
              showIcon
            />

            <Collapse style={{ marginTop: 16 }}>
              <Panel header={queueT('jobDetails.data', 'Job Data')} key="data">
                <JSONPretty id="json-data" data={foundJob.data} theme="monikai" />
              </Panel>
              {foundJob.opts && (
                <Panel header={queueT('jobDetails.options', 'Job Options')} key="options">
                  <JSONPretty id="json-opts" data={foundJob.opts} theme="monikai" />
                </Panel>
              )}
              {foundJob.failedReason && (
                <Panel header={queueT('jobDetails.error', 'Error')} key="error">
                  <Alert message={foundJob.failedReason} type="error" />
                </Panel>
              )}
            </Collapse>
          </div>
        )}
      </Space>
    </Modal>
  );
};

export default JobSearchModal;
